import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Filter, Search, X, Package, ArrowUpCircle, ArrowDownCircle, Users, BarChart3, RefreshCcw, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FootprintMovementReport = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [aggregations, setAggregations] = useState({});
  const [pagination, setPagination] = useState({ skip: 0, limit: 500, total: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    storage_type: '',
    operation_type: '',
    barcode: '',
    staff_name: '',
    bin_location: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(true);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.storage_type) params.append('storage_type', filters.storage_type);
      if (filters.operation_type) params.append('operation_type', filters.operation_type);
      if (filters.barcode) params.append('barcode', filters.barcode);
      if (filters.staff_name) params.append('staff_name', filters.staff_name);
      if (filters.bin_location) params.append('bin_location', filters.bin_location);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      params.append('skip', pagination.skip.toString());
      params.append('limit', pagination.limit.toString());

      const response = await apiClient.get(`/sections/dispatch/footprint-movement-report?${params}`);
      
      setData(response.data.data || []);
      setAggregations(response.data.aggregations || {});
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        showing: response.data.pagination?.showing || 0
      }));
    } catch (error) {
      toast.error('Failed to fetch report');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.skip, pagination.limit]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleClearFilters = () => {
    setFilters({
      storage_type: '',
      operation_type: '',
      barcode: '',
      staff_name: '',
      bin_location: '',
      date_from: '',
      date_to: ''
    });
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.storage_type) params.append('storage_type', filters.storage_type);
      if (filters.operation_type) params.append('operation_type', filters.operation_type);
      if (filters.barcode) params.append('barcode', filters.barcode);
      if (filters.staff_name) params.append('staff_name', filters.staff_name);
      if (filters.bin_location) params.append('bin_location', filters.bin_location);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await apiClient.get(`/sections/dispatch/footprint-movement-report/export-csv?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `footprint_movement_report_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/dispatch')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Footprint Movement Report</h1>
              <p className="text-sm text-gray-500">Track put away and picking operations in FG & SFG storage</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchReport} disabled={isLoading}>
              <RefreshCcw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleExportCSV} className="bg-green-600 hover:bg-green-700">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Aggregation Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Total Operations</p>
                  <p className="text-2xl font-bold">{aggregations.total_records || 0}</p>
                </div>
                <BarChart3 className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Put Away Ops</p>
                  <p className="text-2xl font-bold">{aggregations.total_put_away_ops || 0}</p>
                  <p className="text-xs opacity-70">{aggregations.total_qty_put_away || 0} qty</p>
                </div>
                <ArrowDownCircle className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Picking Ops</p>
                  <p className="text-2xl font-bold">{aggregations.total_picking_ops || 0}</p>
                  <p className="text-xs opacity-70">{aggregations.total_qty_picked || 0} qty</p>
                </div>
                <ArrowUpCircle className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">FG / SFG</p>
                  <p className="text-2xl font-bold">{aggregations.fg_movements || 0} / {aggregations.sfg_movements || 0}</p>
                </div>
                <Warehouse className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs opacity-80">Unique Staff</p>
                  <p className="text-2xl font-bold">{aggregations.unique_staff || 0}</p>
                  <p className="text-xs opacity-70">{aggregations.unique_barcodes || 0} SKUs</p>
                </div>
                <Users className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters & Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg">Movement Records</CardTitle>
              <CardDescription>
                Showing {pagination.showing || data.length} of {pagination.total} records
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </CardHeader>

          {showFilters && (
            <CardContent className="pt-0 pb-4 border-b">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Storage Type</label>
                  <Select
                    value={filters.storage_type}
                    onValueChange={(val) => setFilters(prev => ({ ...prev, storage_type: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="FG">FG Storage</SelectItem>
                      <SelectItem value="SFG">SFG Storage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Operation</label>
                  <Select
                    value={filters.operation_type}
                    onValueChange={(val) => setFilters(prev => ({ ...prev, operation_type: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="put_away">Put Away</SelectItem>
                      <SelectItem value="picking">Picking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Barcode</label>
                  <Input
                    value={filters.barcode}
                    onChange={(e) => setFilters(prev => ({ ...prev, barcode: e.target.value }))}
                    placeholder="Search..."
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Staff Name</label>
                  <Input
                    value={filters.staff_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, staff_name: e.target.value }))}
                    placeholder="Search..."
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Bin Location</label>
                  <Input
                    value={filters.bin_location}
                    onChange={(e) => setFilters(prev => ({ ...prev, bin_location: e.target.value }))}
                    placeholder="Search..."
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Date From</label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Date To</label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                  />
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          )}

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Timestamp</TableHead>
                    <TableHead className="font-semibold">Storage</TableHead>
                    <TableHead className="font-semibold">Barcode</TableHead>
                    <TableHead className="font-semibold">Operation</TableHead>
                    <TableHead className="font-semibold text-right">Qty</TableHead>
                    <TableHead className="font-semibold">Staff Name</TableHead>
                    <TableHead className="font-semibold">Bin Location</TableHead>
                    <TableHead className="font-semibold">Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-gray-500">Loading...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10 text-gray-500">
                        No movement records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="text-sm">
                          {formatDateTime(row.timestamp)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={row.storage_type === 'FG' ? 'default' : 'secondary'}>
                            {row.storage_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.barcode}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={row.operation_type === 'Put Away' 
                              ? 'text-green-700 border-green-300 bg-green-50' 
                              : 'text-orange-700 border-orange-300 bg-orange-50'
                            }
                          >
                            {row.operation_type === 'Put Away' && <ArrowDownCircle className="w-3 h-3 mr-1" />}
                            {row.operation_type === 'Picking' && <ArrowUpCircle className="w-3 h-3 mr-1" />}
                            {row.operation_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{row.qty}</TableCell>
                        <TableCell>{row.staff_name || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{row.bin_location || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-600">{row.slip_no || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex items-center justify-between p-4 border-t">
                <span className="text-sm text-gray-500">
                  Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.skip === 0}
                    onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.skip + pagination.limit >= pagination.total}
                    onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FootprintMovementReport;
